import { z } from 'zod';

export enum TurnstileAccessType {
	Blocked = 'bloqueado',
	InRisk = 'risco',
	Exception = 'excecao',
}

export enum TurnstileAccessTrainingStatus {
	Pendente = 'Pendente',
	Valido = 'Válido',
	ValidoPorEquivalencia = 'Válido por equivalência',
	Vazio = '',
	Vencido = 'Vencido',
	Invalido = 'Inválido',
	VenceEm15Dias = 'Vence em até 15 dias',
	VenceEm30Dias = 'Vence em até 30 dias',
	VenceEm45Dias = 'Vence em até 45 dias',
	MassiveUnblocked = 'Liberação massiva',
	GracePeriod = 'Período de carência',
	InfosEmployeeChangeGracePeriod = 'Mudança na folha',
	ProcedureRevisionGracePeriod = 'Revisão de procedimento',
	BlockGracePeriod = 'Bloqueio em período de carência',
	MatrixChange = 'Mudança de matriz',
	TrainingInDay = 'Treinamento no dia',
}

export const greenStatus = [
	TurnstileAccessTrainingStatus.Valido,
	TurnstileAccessTrainingStatus.ValidoPorEquivalencia,
];

export const darkOrangeStatus = [
	TurnstileAccessTrainingStatus.VenceEm15Dias,
	TurnstileAccessTrainingStatus.GracePeriod,
	TurnstileAccessTrainingStatus.InfosEmployeeChangeGracePeriod,
	TurnstileAccessTrainingStatus.ProcedureRevisionGracePeriod,
	TurnstileAccessTrainingStatus.BlockGracePeriod,
	TurnstileAccessTrainingStatus.MatrixChange,
	TurnstileAccessTrainingStatus.TrainingInDay,
];

export const lightOrangeStatus = [TurnstileAccessTrainingStatus.VenceEm30Dias];

export const yellowStatus = [TurnstileAccessTrainingStatus.VenceEm45Dias];

export const redStatus = [
	TurnstileAccessTrainingStatus.Vencido,
	TurnstileAccessTrainingStatus.Invalido,
	TurnstileAccessTrainingStatus.Pendente,
	TurnstileAccessTrainingStatus.Vazio,
	TurnstileAccessTrainingStatus.MassiveUnblocked,
];

export const TurnstileAccessTrainingSchema = z.object({
	id: z.number(),
	name: z.string(),
	trainingControlId: z.number(),
	trainingType: z.string(),
	status: z.nativeEnum(TurnstileAccessTrainingStatus),
	initialBlockedDate: z.coerce.date().nullable(),
	blockDatePrediction: z.coerce.date().nullable(),
	validityDate: z.coerce.date().nullable(),
	nextTrainingDate: z.coerce.date().nullable(),
});

export const TurnstileAccessEmployeeSchema = z.object({
	id: z.number(),
	name: z.string(),
	alias: z.string().nullable(),
	status: z.string(),
	employeeNumber: z.string(),
	jobTitle: z.string(),
	workStation: z.string(),
	board: z.string(),
	management: z.string(),
	coordination: z.string(),
	trainings: z.array(TurnstileAccessTrainingSchema),
});

export const TurnstileAccessSchema = z.object({
	date: z.coerce.date(),
	employees: z.array(TurnstileAccessEmployeeSchema),
});

export const TurnstileAccessResponseSchema = z.object({
	count: z.coerce.number(),
	nextPage: z.number().optional(),
	turnstileAccess: TurnstileAccessSchema,
});

export type TurnstileAccessTraining = z.infer<
	typeof TurnstileAccessTrainingSchema
>;
export type TurnstileAccessEmployee = z.infer<
	typeof TurnstileAccessEmployeeSchema
>;
export type TurnstileAccess = z.infer<typeof TurnstileAccessSchema>;

const TurnstileAccessFilterSingleOption = z.string().transform((value) => ({
	value,
	label: value.toUpperCase(),
}));

export const TurnstileAccessFilterOptionsSchema = z.object({
	management: z.array(TurnstileAccessFilterSingleOption).default([]),
	workstation: z.array(TurnstileAccessFilterSingleOption).default([]),
	pole: z.array(TurnstileAccessFilterSingleOption).default([]),
	board: z.array(TurnstileAccessFilterSingleOption).default([]),
	jobTitle: z.array(TurnstileAccessFilterSingleOption).default([]),
	coordination: z.array(TurnstileAccessFilterSingleOption).default([]),
	employee: z.array(TurnstileAccessFilterSingleOption).default([]),
	employeeStatus: z.array(TurnstileAccessFilterSingleOption).default([]),
	training: z.array(TurnstileAccessFilterSingleOption).default([]),
});

export type TurnstileAccessFilterOptions = z.infer<
	typeof TurnstileAccessFilterOptionsSchema
>;

export const getExceptionMotiveValue = (
	trainings: TurnstileAccessTraining[],
) => {
	if (trainings.length === 0) {
		return 'TREINAMENTO';
	}
	
	const uniqueStatuses = [
		...new Set(trainings.map((training) => training.status)),
	];

	if (uniqueStatuses.length > 1) {
		return 'DIVERSOS';
	}

	const status = uniqueStatuses[0];

	switch (status) {
		case TurnstileAccessTrainingStatus.GracePeriod:
			return 'NOVO COLABORADOR';
		case TurnstileAccessTrainingStatus.InfosEmployeeChangeGracePeriod:
			return 'MUDANÇAS DE CARGO/LOCAL DE TRABALHO'; //coordenação tambem
		case TurnstileAccessTrainingStatus.MassiveUnblocked:
			return 'LIBERAÇÃO MASSIVA';
		case TurnstileAccessTrainingStatus.ProcedureRevisionGracePeriod:
			return 'REVISÃO DE PROCEDIMENTO';
		case TurnstileAccessTrainingStatus.BlockGracePeriod:
			return 'BLOQUEIO EM PERÍODO DE CARÊNCIA';
		case TurnstileAccessTrainingStatus.MatrixChange:
			return 'MUDANÇA DE MATRIZ';
		case TurnstileAccessTrainingStatus.TrainingInDay:
			return 'TREINAMENTO NO DIA';
		case TurnstileAccessTrainingStatus.Valido:
			return 'TREINAMENTO VÁLIDO';
		default:
			return 'TREINAMENTO';
	}
};
